/**
 * Authors: mariushirjanu, triadiprabowo
 * Created: 28/06/2021
 */
import * as _ from 'underscore';
import { util } from '../../../shared/util';
import { ICompanyCountry, IContact, ILabelledValue, IValueObject } from '../../../../type_definitions/datatypes';
import { CompanyType } from '../../../shared/company/company-data.service';
import { Injectable } from '@angular/core';
import { TranslationService } from '../../../shared/translation/translation.service';
import { IRoleContact, IWebshopInfo, IWebshopInfoData} from './webshop-info-data.service';

import { RoleContactType } from '../../../shared/contact/role-contact-type';

@Injectable()
export class WebshopInfoService {
  private _webshopInfoData:IWebshopInfoData;
  private _gbdm:IContact;

  constructor(
    private translationService:TranslationService
  ) {
    /***/
  }

  public createWebshopInfoModel(data:IWebshopInfoData):IWebshopInfo {
    this._webshopInfoData = util.copyAndExtend(data);
    this.gbdm = _.clone(data.gbdmInfo?.webshopContact?.contact); // make a copy of the saved GBDM

    return {
      customerCompany: data.customerInfo?.company?.name,
      customerMainContact: data.customerInfo?.mainContact?.contact,
      customerCompanyHQ: data.customerInfo?.companyHQ,
      customerSector: data.customerInfo?.company?.activitySector,
      customerSectorComment: data.customerInfo?.company?.activitySectorDescription,
      customerHomepage: data.customerInfo?.homepage,
      webshopName: data.webshop?.name,
      inProduction: data.webshop?.inProduction,
      isMainWebshop: data.webshop?.isMainWebshop,
      webshopDescription: data.webshop?.description,
      webshopType: data.webshop?.type,
      orderProcess: data.webshop?.orderProcess,
      productProcurement: data.webshop?.productProcurement,
      localServiceRFP: data.webshop?.localServiceRFP,
      opportunityType: data.opportunityAssessment?.opportunity,
      winChances: data.opportunityAssessment?.winChances,
      competitors: data.opportunityAssessment?.competitors,
      incumbentOemPartner: data.opportunityAssessment?.incumbentOemPartner,
      detractors: data.opportunityAssessment?.detractors,
      expectedDecisionDate: data.opportunityAssessment?.expectedDecisionDate,
      expectedStartDate: data.opportunityAssessment?.expectedStartDate,
      expectedOrdersPerYear: data.opportunityAssessment?.expectedOrdersPerYear,
      revenueForecast: data.opportunityAssessment?.revenueForecastUSD,
      oemCatalogAvailable: _.findWhere(this.getBooleanValues(), { value: data.catalogInfo?.oemCatalogAvailable }),
      bpCatalogActivatedQty: data.catalogInfo?.bpCatalogActivatedQty,
      bpCatalogNotActivatedQty: data.catalogInfo?.bpCatalogNotActivatedQty,
      expectedCatalogUploadDate: data.catalogInfo?.expectedCatalogUploadDate,
      catalogUploadComment: data.catalogInfo?.catalogUploadComment,
      userAddressListAvailable: _.findWhere(this.getBooleanValues(), { value: data.opportunityAssessment?.userListAvailable }),
      expectedUserUploadDate: data.opportunityAssessment?.expectedUserListUploadDate,
      userListUploadComment: data.opportunityAssessment?.userListUploadComment,
      webshopStatus: data.webshop?.status,
      webshopStatusDate: data.webshop?.statusDate,
      webshopStatusComments: data.webshop?.statusComment,
      webshopLastUpdateComments: data.webshop?.statusCommentDate,
      focusContactType: data.accountManagement?.focusContactType,
      focusContact: undefined,
      amTime: data.accountManagement?.amTime,
      amType: data.accountManagement?.accountManagementType,
      timeSlot: data.accountManagement?.timeSlot,
      orderManagementType: data.accountManagement?.orderManagementType,
      orderUpdateRoleContacts: data.accountManagement?.orderUpdateRoleContacts,
      orderUpdateContacts: this.roleContactsToContacts(data.accountManagement?.orderUpdateRoleContacts),
      isMainOem: data.oemInfo?.mainOem,
      oem: data.oemInfo?.company,
      oemRoleContacts: data.oemInfo?.roleContacts,
      oemMainContacts: this.roleContactsToContacts(data.oemInfo?.roleContacts),
      oemCustomerType: data.oemInfo?.customerType,
      customerIdWithOem: data.oemInfo?.customerIdWithOem,
      lamType: _.findWhere(this.getLamTypes(), { id: (<IValueObject>data.lam?.type)?.id }),
      lamCustomerCountry: data.lam?.bpOrCustomerCompanyCountry?.country,
      lamCompanyCountry: data.lam?.bpOrCustomerCompanyCountry,
      lamCompanyDepartment: data.lam?.oemCompanyDepartment,
      lamCustomerRelation: data.lam?.customerRelation,
      lamComments: data.lam?.comments,
      lamRoleContacts: data.lam?.roleContacts,
      lamMainContacts: this.roleContactsToContacts(data.lam?.roleContacts),
      activeCustomers: data.lam?.activeCustomers,
      totalCustomers: data.lam?.totalCustomers,
      superLAM: data.lam?.superLAM,
      gbdm: data.gbdmInfo?.webshopContact?.contact,
      gbdmCompany: data.gbdmInfo?.webshopContact?.contact?.company,
      addressHQ: data.customerInfo?.companyHQ,
      expectedDeskTransferDate: data.gbdmInfo?.expectedDeskTransferDate,
      spotlight: data.customerInfo?.spotlight
    };
  }

  public createWebshopInfoRequest(data:IWebshopInfoData, model:IWebshopInfo, lam:ICompanyCountry):IWebshopInfoData {
    const requestData:IWebshopInfoData = _.extend({}, data);

    requestData.customerInfo.company.name = model.customerCompany;
    requestData.customerInfo.company.activitySectorDescription = model.customerSectorComment;
    requestData.customerInfo.mainContact = model.customerMainContact ? this.contactsToRoleContacts([<IContact>model.customerMainContact], RoleContactType.CGAM)?.[0] : undefined;
    requestData.customerInfo.companyHQ = model.customerCompanyHQ;
    requestData.customerInfo.company.activitySector = model.customerSector;
    requestData.customerInfo.homepage = model.customerHomepage;
    requestData.customerInfo.spotlight = model.spotlight;

    requestData.webshop.name = model.webshopName;
    requestData.webshop.inProduction = model.inProduction;
    requestData.webshop.isMainWebshop = model.isMainWebshop;
    requestData.webshop.description = model.webshopDescription;
    requestData.webshop.type = model.webshopType;
    requestData.webshop.productProcurement = model.productProcurement;
    requestData.webshop.orderProcess = model.orderProcess;
    requestData.webshop.localServiceRFP = model.localServiceRFP;
    requestData.webshop.status = model.webshopStatus;
    requestData.webshop.statusComment = model.webshopStatusComments;

    requestData.opportunityAssessment.opportunity = model.opportunityType;
    requestData.opportunityAssessment.winChances = model.winChances;
    requestData.opportunityAssessment.competitors = model.competitors;
    requestData.opportunityAssessment.incumbentOemPartner = model.incumbentOemPartner;
    requestData.opportunityAssessment.detractors = model.detractors;
    requestData.opportunityAssessment.expectedDecisionDate = model.expectedDecisionDate;
    requestData.opportunityAssessment.expectedStartDate = model.expectedStartDate;
    requestData.opportunityAssessment.expectedOrdersPerYear = model.expectedOrdersPerYear;
    requestData.opportunityAssessment.revenueForecastUSD = model.revenueForecast;
    requestData.opportunityAssessment.userListAvailable = util.extractValue(model.userAddressListAvailable);
    requestData.opportunityAssessment.expectedUserListUploadDate = model.expectedUserUploadDate;
    requestData.opportunityAssessment.userListUploadComment = model.userListUploadComment;

    requestData.accountManagement.focusContactType = model.focusContactType;
    requestData.accountManagement.accountManagementType = model.amType;
    requestData.accountManagement.amTime = model.amTime;
    requestData.accountManagement.timeSlot = model.timeSlot;
    requestData.accountManagement.orderManagementType = model.orderManagementType;
    requestData.accountManagement.orderUpdateRoleContacts = this.contactsToRoleContacts(model.orderUpdateContacts, RoleContactType.ORDER_UPDATE);

    requestData.oemInfo.mainOem = model.isMainOem;
    requestData.oemInfo.company = model.oem;
    requestData.oemInfo.roleContacts = this.contactsToRoleContacts(model.oemMainContacts, RoleContactType.VGAM);
    requestData.oemInfo.customerType = model.oemCustomerType;
    requestData.oemInfo.customerIdWithOem = model.customerIdWithOem;

    requestData.catalogInfo.oemCatalogAvailable = util.extractValue(model.oemCatalogAvailable);
    requestData.catalogInfo.expectedCatalogUploadDate = model.expectedCatalogUploadDate;
    requestData.catalogInfo.catalogUploadComment = model.catalogUploadComment;

    requestData.lam.type = model.lamType;

    if (model.lamType.id === CompanyType.BP) {
      requestData.lam.bpOrCustomerCompanyCountry = model.lamCompanyCountry;
      requestData.lam.oemCompanyDepartment = undefined;
    } else if (model.lamType.id === CompanyType.OEM) {
      requestData.lam.oemCompanyDepartment = model.lamCompanyDepartment;
      requestData.lam.bpOrCustomerCompanyCountry = undefined;
    } else {
      requestData.lam.oemCompanyDepartment = undefined;
      requestData.lam.bpOrCustomerCompanyCountry = lam;
    }

    requestData.lam.customerRelation = model.lamCustomerRelation;
    requestData.lam.comments = model.lamComments;
    requestData.lam.roleContacts = this.contactsToRoleContacts(model.lamMainContacts, RoleContactType.LAM);
    requestData.lam.superLAM = model.superLAM;

    requestData.gbdmInfo.webshopContact = model.gbdm ? this.contactsToRoleContacts([model.gbdm], RoleContactType.GBDM)?.[0] : undefined;
    requestData.gbdmInfo.expectedDeskTransferDate = model.expectedDeskTransferDate;

    return requestData;
  }

  public getBooleanValues():ILabelledValue[] {
    return [
      {
        value: true,
        label: this.translationService.instant('GA.GENERAL.YES')
      },
      {
        value: false,
        label: this.translationService.instant('GA.GENERAL.NO')
      }
    ];
  }

  public getLamTypes():IValueObject[] {
    return [
      {
        id: CompanyType.BP,
        value: 'BP'
      },
      {
        id: CompanyType.OEM,
        value: 'OEM'
      },
      {
        id: CompanyType.CUSTOMER,
        value: 'GA.GENERAL.CUSTOMER'
      }
    ];
  }

  private roleContactsToContacts(roleContacts:IRoleContact[]):IContact[] {
    const contacts:IContact[] = [];
    _.each(roleContacts, (roleContact:IRoleContact) => {
      contacts.push(roleContact.contact);
    });
    return contacts;
  }

  private contactsToRoleContacts(contacts:IContact[], contactType:RoleContactType):IRoleContact[] {
    const roleContacts:IRoleContact[] = [];

    const assignedRoleContacts:IRoleContact[] = this.getRoleContactsByContactType(contactType);
    _.each(contacts, (contact:IContact) => {
      const foundRoleContact:IRoleContact = _.find(assignedRoleContacts, (initialRoleContact:IRoleContact) => initialRoleContact?.contact?.id === contact?.id); // find contact in the initial role contacts
      if (!foundRoleContact) {
        // create a new role contact
        roleContacts.push({
          contact: contact,
          contactType,
          creationDate: new Date().getTime()
        });
      } else {
        // use the existing role contact
        roleContacts.push(foundRoleContact);
      }
    });

    return roleContacts;
  }

  public diffContactsInRoles(selectedContacts:IContact[], contactType:RoleContactType):{ contacts:IContact[]; roleContacts:IRoleContact[] } {
    const roleContacts:IRoleContact[] = [];
    const contacts:IContact[] = [];

    const assignedRoleContacts:IRoleContact[] = this.getRoleContactsByContactType(contactType);

    _.each(selectedContacts, (newContact:IContact) => {
      const contactInRole:IRoleContact = _.find(assignedRoleContacts, (lamRoleContact:IRoleContact) => newContact?.id && lamRoleContact?.contact?.id === newContact?.id);
      if (!!contactInRole) {
        roleContacts.push(contactInRole);
      } else {
        contacts.push(newContact);
      }
    });

    return {
      contacts,
      roleContacts
    };
  }

  private getRoleContactsByContactType(contactType:RoleContactType):IRoleContact[] {
    switch (contactType) {
      case RoleContactType.LAM:
        return this._webshopInfoData?.lam?.roleContacts || [];
      case RoleContactType.VGAM:
        return this._webshopInfoData?.oemInfo?.roleContacts || [];
      case RoleContactType.ORDER_UPDATE:
        return this._webshopInfoData?.accountManagement?.orderUpdateRoleContacts || [];
      case RoleContactType.CGAM:
        return _.compact([this._webshopInfoData?.customerInfo?.mainContact]);
      case RoleContactType.GBDM:
        return _.compact([this._webshopInfoData?.gbdmInfo?.webshopContact]);
    }
  }

  public set gbdm(gbdm:IContact) {
    this._gbdm = gbdm;
  }

  public get gbdm():IContact {
    return this._gbdm;
  }

  public clear():void {
    this._webshopInfoData = undefined;
    this._gbdm = undefined;
  }
}